/**
 * Gedeelde admin sectie waarmee per tool de "Boek een meeting" CTA kan
 * worden aan-/uitgezet en aangepast.
 *
 * Gebruik in een tool-manager:
 *   <BookingCtaSettings cfg={config} onChange={(field, value) =>
 *     setConfig(c => ({ ...c, [field]: value }))} />
 *
 * Bij het opslaan: stuur de 4 velden mee in de PUT payload.
 */
import { CalendarCheck, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { DEFAULT_BOOKING_URL } from '@/components/BookingCta';

export default function BookingCtaSettings({ cfg, onChange }) {
  if (!cfg) return null;
  const enabled = cfg.booking_cta_enabled !== false;
  const url = (cfg.booking_url || '').trim() || DEFAULT_BOOKING_URL;
  return (
    <Card className="border-emerald-100">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-semibold flex items-center gap-2 text-slate-900">
              <CalendarCheck className="w-4 h-4 text-emerald-600" /> Boek een meeting CTA
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Toont een knop op het resultaten-scherm van deze tool die naar de boeking-URL leidt.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={enabled}
              onCheckedChange={(v) => onChange('booking_cta_enabled', v)}
              data-testid="booking-cta-toggle"
            />
            <Label className="text-sm">CTA actief</Label>
          </div>
        </div>

        <div>
          <Label className="text-xs">Knoptekst / titel</Label>
          <Input
            value={cfg.booking_cta_label || ''}
            onChange={(e) => onChange('booking_cta_label', e.target.value)}
            placeholder="Plan een gesprek"
            data-testid="booking-cta-label-input"
          />
        </div>

        <div>
          <Label className="text-xs">Korte uitleg / subtitel</Label>
          <Textarea
            rows={2}
            value={cfg.booking_cta_subtitle || ''}
            onChange={(e) => onChange('booking_cta_subtitle', e.target.value)}
            placeholder="Bespreek je resultaten 1-op-1 met een Zynq-expert."
            data-testid="booking-cta-subtitle-input"
          />
        </div>

        <div>
          <Label className="text-xs">Boeking URL</Label>
          <div className="flex items-center gap-2 mt-1.5">
            <Input
              value={cfg.booking_url || ''}
              onChange={(e) => onChange('booking_url', e.target.value)}
              placeholder={DEFAULT_BOOKING_URL}
              className="font-mono text-xs"
              data-testid="booking-cta-url-input"
            />
            <Button variant="outline" size="icon" asChild title="Open URL">
              <a href={url} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-4 h-4" />
              </a>
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1">
            Default: <code className="text-[10px]">{DEFAULT_BOOKING_URL}</code>. Wordt ook gebruikt
            voor de QR-code in eventuele PDF-rapporten van deze tool.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
